import { type FpB2bProductCatalogDTO, type MergedCoupon } from '@cart-services/types'
import { CouponType } from '@hooks-api/types/dataModel/coupon'
import { type CrossBrandMallHomeCampaign } from '@hooks-api/types/dataModel/cross-brand-mall'
import { PRODUCT_CARD_BADGE, type ProductCardEntry } from '../types/product-card-entry'
import { buildProductCardEntry } from './build-product-card-entry'

interface GetCouponEligibleProductCardsParams {
  productCatalogDto: FpB2bProductCatalogDTO | undefined
  mergedCouponMap: Map<number, MergedCoupon> | undefined
  selectedCouponId: number | null
}

/**
 * 取得目前選中的 coupon 所適用的 product cards。
 *
 * 全店券（CouponType.ALL_PRODUCT）回傳 product 底下所有 variations；
 * 其他類型則只挑出 `coupon_ids` 包含選中 coupon 的 variations。
 * 沒有任何適用 variation 的 product 會被排除。
 *
 * 上游 catalog 目前保證一個 product 只會綁定一個 category，所以兩層 flatMap 不會
 * 產生重複卡片；若未來上游改為跨類複掛，需要在此補去重邏輯。
 *
 * 顯示筆數上限由呼叫端決定，本函式回傳完整候選清單。
 */
export const getCouponEligibleProductCards = (params: GetCouponEligibleProductCardsParams): ProductCardEntry[] => {
  const { productCatalogDto, mergedCouponMap, selectedCouponId } = params

  if (!productCatalogDto || !mergedCouponMap || selectedCouponId === null) return []

  const isAllProductCoupon = mergedCouponMap.get(selectedCouponId)?.couponTypes.has(CouponType.ALL_PRODUCT) ?? false

  return productCatalogDto.products
    .flatMap(category => category.all_product)
    .flatMap((product) => {
      const eligibleVariations = isAllProductCoupon
        ? product.variations
        : product.variations.filter(variation => variation.coupon_ids?.includes(selectedCouponId))

      if (eligibleVariations.length === 0) return []

      return [buildProductCardEntry({
        product,
        eligibleVariations,
        badge: PRODUCT_CARD_BADGE.SALE,
      })]
    })
}

interface GetHighlightCouponsParams {
  campaign: CrossBrandMallHomeCampaign
  mergedCouponMap: Map<number, MergedCoupon> | undefined
}

/**
 * 取得 campaign 設定的 highlight coupons，依 `display_order` 由小到大排序。
 *
 * 每個 highlight 透過 `mergedCouponMap` 反查成 MergedCoupon；反查不到的項目會被略過
 */
export const getHighlightCoupons = (params: GetHighlightCouponsParams): MergedCoupon[] => {
  const { campaign, mergedCouponMap } = params
  if (!mergedCouponMap) return []
  return [...campaign.highlight_coupons]
    .sort((a, b) => a.display_order - b.display_order)
    .flatMap((highlight) => {
      const coupon = mergedCouponMap.get(highlight.coupon_id)
      return coupon ? [coupon] : []
    })
}
